import type { ClipboardEvent, FormEvent, RefObject } from 'react';
import { inertDocument } from '@/utils/inertDocument.ts';
import { plainCopy } from './plainCopy.ts';

export interface PasteAreaProps {
  /** The box itself, so the page can fill it on paste and empty it on reset. */
  areaRef: RefObject<HTMLDivElement | null>;
  /** The markup or text that was pasted, and the plain text inside it. */
  onChange: (pasted: string, text: string) => void;
}

/**
 * Where the list is dropped.
 *
 * A contenteditable box rather than a textarea, because what people paste is a
 * table — out of Excel, Outlook or a browser — and only the HTML flavour of the
 * clipboard keeps its cells apart. A textarea would see the flattened text and
 * lose the column boundaries the parser reads. The box is uncontrolled: it
 * shows a text-only copy of what was pasted, and React is told what arrived
 * rather than told what to draw.
 */
export function PasteArea({ areaRef, onChange }: PasteAreaProps) {
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    const area = areaRef.current;

    if (html) {
      /* The text is what the Parse button reads to know there is something. */
      const pasted = inertDocument(html);
      area?.replaceChildren(plainCopy(pasted));
      onChange(html, pasted.body.textContent ?? '');
      return;
    }

    if (area) area.innerText = text;
    onChange(text, text);
  };

  const handleInput = (event: FormEvent<HTMLDivElement>) => {
    const text = event.currentTarget.innerText;
    onChange(text, text);
  };

  return (
    <div
      ref={areaRef}
      id='pastedData'
      role='textbox'
      aria-multiline='true'
      aria-label='Paste user data'
      contentEditable
      suppressContentEditableWarning
      onPaste={handlePaste}
      onInput={handleInput}
      className='min-h-50 w-full overflow-auto rounded-md border border-line bg-surface px-3.5 py-2.75 font-mono text-sm whitespace-pre-wrap text-ink outline-none focus:shadow-focus'
    />
  );
}
